import { GenericService } from '@/services/api/generic.service';
import { Organisation, PaginatedOrganisationResponse } from '@/models';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

export interface OrganisationSearchParams {
  page?: number;
  per_page?: number;
  search?: string;
  direct_only?: boolean;
}

const isPaginatedOrganisationResponse = (value: unknown): value is PaginatedOrganisationResponse<Organisation[]> => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const response = value as { data?: unknown; meta?: unknown };
  return Array.isArray(response.data) && typeof response.meta === 'object' && response.meta !== null;
};

const isAxiosPaginatedOrganisationResponse = (
  value: unknown
): value is AxiosResponse<PaginatedOrganisationResponse<Organisation[]>> => {
  if (!value || typeof value !== 'object' || !('data' in value)) {
    return false;
  }

  const response = value as { data?: unknown };
  return isPaginatedOrganisationResponse(response.data);
};

export class DomainOrganisationService extends GenericService<Organisation> {

  constructor(domainId: string) {
    super(`v1/domains/${domainId}/organisations`);
  }

  async getOrganisationsWithHierarchy(params?: AxiosRequestConfig): Promise<Organisation[]> {
    try {
      const response = await this.index({
        ...params,
        params: {
          ...params?.params,
          include: 'domain,orderConfiguration'
        }
      });

      if (response === null) {
        return null;
      }

      const organisations = 'data' in response ? response.data : response as Organisation[];
      return organisations;
    } catch (error: any) {
      if (error.response?.status === 403) {
        throw new Error('Access denied to organisation details');
      } else if (error.response?.status === 404) {
        throw new Error('Domain not found');
      }
      throw error;
    }
  }

  async getPaginatedOrganisations(
    config?: AxiosRequestConfig,
    searchParams: OrganisationSearchParams = {}
  ): Promise<PaginatedOrganisationResponse<Organisation[]>> {
    const response = await this.index({
      ...config,
      params: {
        ...config?.params,
        page: searchParams.page ?? 1,
        per_page: searchParams.per_page ?? 10,
        search: searchParams.search || null,
        direct_only: searchParams.direct_only ?? true
      }
    });

    if (isPaginatedOrganisationResponse(response)) {
      return response;
    }

    if (isAxiosPaginatedOrganisationResponse(response)) {
      return response.data;
    }

    throw new Error('Unexpected paginated organisation response shape');
  }
}
